import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaThumbtack } from 'react-icons/fa6';
import { HiOutlineEllipsisVertical } from 'react-icons/hi2';
import { NS } from '../U3204';
import type { U3204ConversationSearchRow } from '../U3204Service';
import { u3204ResolveConversationDisplayTitle } from '../utils/u3204ResolveConversationDisplayTitle';

export type U3204ConversationListItemVariant = 'active' | 'archived';

export interface U3204ConversationListItemProps {
  conversation: U3204ConversationSearchRow;
  isActive: boolean;
  variant: U3204ConversationListItemVariant;
  showMissionHint?: boolean;
  enableReorder?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onSelect: (id: string) => void;
  onRename?: (conversation: U3204ConversationSearchRow) => void;
  onPin?: (conversation: U3204ConversationSearchRow) => void;
  onUnpin?: (conversation: U3204ConversationSearchRow) => void;
  onMoveUp?: (conversation: U3204ConversationSearchRow) => void;
  onMoveDown?: (conversation: U3204ConversationSearchRow) => void;
  onArchive?: (conversation: U3204ConversationSearchRow) => void;
  onUnarchive?: (conversation: U3204ConversationSearchRow) => void;
  onDelete: (conversation: U3204ConversationSearchRow) => void;
}

const U3204ConversationListItem: React.FC<U3204ConversationListItemProps> = ({
  conversation,
  isActive,
  variant,
  showMissionHint = false,
  enableReorder = false,
  canMoveUp = false,
  canMoveDown = false,
  onSelect,
  onRename,
  onPin,
  onUnpin,
  onMoveUp,
  onMoveDown,
  onArchive,
  onUnarchive,
  onDelete,
}) => {
  const { t } = useTranslation(NS);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const title = u3204ResolveConversationDisplayTitle(conversation.title, t('newConversation', { ns: NS }));

  const menuItems =
    variant === 'active'
      ? [
          ...(enableReorder && conversation.pinned
            ? [
                {
                  key: 'moveUp',
                  label: t('moveUp', { ns: NS }),
                  onClick: () => onMoveUp?.(conversation),
                  disabled: !canMoveUp,
                },
                {
                  key: 'moveDown',
                  label: t('moveDown', { ns: NS }),
                  onClick: () => onMoveDown?.(conversation),
                  disabled: !canMoveDown,
                },
              ]
            : []),
          ...(conversation.pinned
            ? [{ key: 'unpin', label: t('unpin', { ns: NS }), onClick: () => onUnpin?.(conversation) }]
            : [{ key: 'pin', label: t('pin', { ns: NS }), onClick: () => onPin?.(conversation) }]),
          { key: 'rename', label: t('rename', { ns: NS }), onClick: () => onRename?.(conversation) },
          { key: 'archive', label: t('archive', { ns: NS }), onClick: () => onArchive?.(conversation) },
          { key: 'delete', label: t('delete', { ns: NS }), onClick: () => onDelete(conversation), danger: true },
        ]
      : [
          { key: 'unarchive', label: t('unarchive', { ns: NS }), onClick: () => onUnarchive?.(conversation) },
          { key: 'delete', label: t('delete', { ns: NS }), onClick: () => onDelete(conversation), danger: true },
        ];

  return (
    <div className="relative">
      <div
        className={`flex w-full items-start gap-1 rounded-lg px-2 py-1 transition-colors hover:bg-muted ${
          isActive ? 'bg-muted' : ''
        }`}
      >
        <button
          type="button"
          onClick={() => onSelect(conversation.id)}
          className={`min-w-0 flex-1 rounded-md px-1 py-1 text-left text-sm ${
            isActive ? 'font-medium' : ''
          }`}
        >
          <span className="flex items-start gap-1.5">
            {conversation.pinned ? (
              <FaThumbtack
                className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground"
                aria-hidden
              />
            ) : null}
            <span className="line-clamp-2">{title}</span>
          </span>
          {showMissionHint && conversation.mission ? (
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {conversation.mission.code} — {conversation.mission.title}
            </span>
          ) : null}
        </button>
        <div ref={menuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((open) => !open);
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-background hover:text-foreground"
            aria-label={t('conversationActions', { ns: NS })}
            aria-expanded={menuOpen}
          >
            <HiOutlineEllipsisVertical className="h-4 w-4" />
          </button>
          {menuOpen ? (
            <div
              role="menu"
              className="absolute right-0 z-20 mt-1 min-w-40 rounded-lg border border-border bg-surface py-1 shadow-lg"
              onClick={(event) => event.stopPropagation()}
            >
              {menuItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={'disabled' in item ? item.disabled : false}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 ${
                    'danger' in item && item.danger ? 'text-destructive' : 'text-foreground'
                  }`}
                  onClick={() => {
                    if ('disabled' in item && item.disabled) return;
                    setMenuOpen(false);
                    item.onClick();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default U3204ConversationListItem;
